import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { ContextMenuModule } from 'primeng/contextmenu';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FieldsetModule } from 'primeng/fieldset';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TreeSelectModule } from 'primeng/treeselect';
import { TreeTableModule } from 'primeng/treetable';

import { firstValueFrom, forkJoin } from 'rxjs';

import { SETTINGS, Settings } from '../../../../app.settings';

import { notifyAndTrackError } from '../../../../common/error';
import { successMsg } from '../../../../common/messages';

import { AuditInfoComponent, CanComponentDeactivate } from '../../../../components';

import { AppInsightsService } from '../../../../services/app-insights.service';
import { DeliveryDayAdminService, IDeliveryDay } from '../../../../services/insecta';

@Component({
  selector: 'tab-delivery-days',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    BreadcrumbModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ContextMenuModule,
    CommonModule,
    DatePickerModule,
    DialogModule,
    FloatLabelModule,
    FieldsetModule,
    FormsModule,
    InputTextModule,
    RouterModule,
    SelectModule,
    TableModule,
    TabsModule,
    TextareaModule,
    ToggleSwitchModule,
    TreeSelectModule,
    TreeTableModule,
    AuditInfoComponent
  ],

  templateUrl: 'tab-delivery-days.component.html'
})
export class TabDeliveryDaysComponent implements OnDestroy, OnInit, CanComponentDeactivate {
  dirtyConfirm$: ((leave: boolean) => void) | null = null;
  emptyGuid: string = `00000000-0000-0000-0000-000000000000`;
  pageVars: any = {
    breadcrumbItems: <MenuItem[]>[{ label: 'Global Lists', routerLink: '/admin/globallists' }, { label: 'Site Catalog & Settings' }],
    isDirty: false,
    isEditing: false,
    isReady: false,
    pageId: `Admin:SiteCatalog`,
    showDirtyDlg: false
  };

  @Input() locationId: string = this.emptyGuid;
  @Input() currentUser: string = ``;
  @Output('isReady') isReady = new EventEmitter<boolean>();

  deliveryDays: IDeliveryDay[] = [];

  constructor(
    private deliveryDayService: DeliveryDayAdminService,
    private appInsights: AppInsightsService,
    private msgService: MessageService,
    @Inject(SETTINGS) private settings: Settings
  ) {
    this.pageVars.pageId = `${settings.environment}:Admin:SiteCatalog:DeliveryDaysTab`;
    appInsights.trackPageView(this.pageVars.pageId, `/admin/sitecatalog#tabdeliverydays`);
  }

  canDeactivate(): Promise<boolean> {
    if (!this.pageVars.isEditing) return Promise.resolve(true);

    return new Promise((resolve) => {
      this.pageVars.showDirtyDlg = true;
      const confirm$ = (decision: boolean) => {
        this.dirtyConfirm$ = null;
        resolve(decision);
      };
      this.dirtyConfirm$ = confirm$;
    });
  }

  dirtyConfirm(leave: boolean) {
    this.pageVars.showDirtyDlg = false;
    if (this.dirtyConfirm$) {
      this.dirtyConfirm$(leave);
    }
  }

  ngOnDestroy() {
    this.locationId = this.emptyGuid;
    this.pageVars = {
      isReady: false
    };
    this.deliveryDays = [];
  }

  ngOnInit() {
    this.isReady.emit(false);
    this.appInsights.trackEvent(`${this.pageVars.pageId}:Init`);
    this.pageVars.isReady = false;

    const request = {
      deliveryDays: this.deliveryDayService.getDeliveryDays(this.locationId)
    };

    firstValueFrom(forkJoin(request))
      .then((response) => {
        this.deliveryDays = response.deliveryDays;
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabDeliveryDaysComponent', action: 'Init:Error' });
      })
      .finally(() => {
        this.pageVars.isReady = true;
        this.pageVars.isEditing = false;
        this.isReady.emit(true);
      });
  }

  tglDeliveryDay(d: IDeliveryDay) {
    firstValueFrom(this.deliveryDayService.updateDeliveryDay(this.locationId, d.dayId, d.isActive))
      .then(() => {
        const idx = this.deliveryDays.findIndex((x) => x.dayId === d.dayId);
        this.deliveryDays[idx].action = 'UPD';
        this.deliveryDays[idx].actionBy = this.currentUser;
        this.deliveryDays[idx].actionOn = new Date();

        successMsg(this.msgService, 'Delivery Day has been updated');
      })
      .catch((err) => {
        notifyAndTrackError(this.msgService, this.appInsights, err, { component: 'TabDeliveryDaysComponent', action: 'ToggleDeliveryDay:Error' });
      });
  }
}
